import React from 'react';

import { useIntl } from 'react-intl';

import { cashCollectionsMessages } from 'src/features/cashCollections/utils/messages';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useGetPayStation } from 'src/features/payStations/api/useGetPaystation';
import { CURRENCIES } from 'src/features/payStations/types';
import {
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { DialogSeparator } from 'src/registry/ui/Dialog';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';

import type { UseFormReturn } from 'react-hook-form';
import type { CreateCashCollectionFormSchemaValues } from 'src/features/cashCollections/hooks/useCreateCashCollection';

type CashCollectionBreakdownProps = {
    form: UseFormReturn<CreateCashCollectionFormSchemaValues>;
};

const CashCollectionBreakdown = ({ form }: CashCollectionBreakdownProps) => {
    const { formatMessage, formatNumber } = useIntl();

    const selectedPayStationId = form.watch('paystation');
    const selectedParkingId = form.watch('parking');
    const selectedType = form.watch('type');

    const { data: parkingData } = useGetParking({
        parkingId: selectedParkingId,
        config: { enabled: !!selectedParkingId },
    });

    const parkingCurrency = React.useMemo(() => {
        return parkingData?.default_currency as CURRENCIES;
    }, [parkingData, selectedParkingId]);

    const { data: selectedPayStation } = useGetPayStation({
        payStationId: selectedPayStationId,
        config: { enabled: !!selectedPayStationId },
    });

    const { acceptedNominalAmounts, normalizeKey } = React.useMemo(() => {
        const acceptedBanknotes = selectedPayStation?.settings?.acceptedBanknotes;
        const acceptedCoins = selectedPayStation?.settings?.acceptedCoins;

        const banknotesToReturn = selectedPayStation?.settings?.banknotesToReturn;
        const coinsToReturn = selectedPayStation?.settings?.coinsToReturn;

        const banknotesBasedOnCollectionType = selectedType === 'collect' ? acceptedBanknotes : banknotesToReturn;
        const coinsBasedOnCollectionType = selectedType === 'collect' ? acceptedCoins : coinsToReturn;

        const banknotesBasedOnCurrency = banknotesBasedOnCollectionType?.[parkingCurrency]?.split(',') ?? [];
        const coinsBasedOnCurrency = coinsBasedOnCollectionType?.[parkingCurrency]?.split(',') ?? [];

        const acceptedNominalAmountsList = [...coinsBasedOnCurrency, ...banknotesBasedOnCurrency];
        const acceptedNominalAmounts = acceptedNominalAmountsList.filter((nominal) => nominal !== '');

        const normalizeKey = (denom: string) => denom.replace(/\./g, '_');

        return { acceptedNominalAmounts, normalizeKey };
    }, [selectedPayStation, form.watch('type'), parkingCurrency]);

    return selectedType !== undefined ? (
        <>
            <FormFieldBlockLabel>
                <FormFieldBlockTitle>
                    <FormFieldBlockName>{formatMessage(cashCollectionsMessages.cashBreakdown)}</FormFieldBlockName>
                </FormFieldBlockTitle>
            </FormFieldBlockLabel>
            <DialogSeparator />
            {acceptedNominalAmounts?.map((denom) => {
                const normalizedKey = normalizeKey(denom);
                return (
                    <FormField
                        key={denom}
                        control={form.control}
                        name={`nominal_amounts.${normalizedKey}`}
                        render={({ field }) => (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog" required>
                                    {formatNumber(parseFloat(denom), {
                                        style: 'currency',
                                        currency: parkingCurrency,
                                    })}
                                </FormLabel>
                                <FormControlStack className="mr-3">
                                    <FormControl>
                                        <div className="flex gap-2">
                                            <Input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                {...field}
                                                value={field.value ?? ''}
                                                onChange={(e) => {
                                                    const onlyDigits = e.target.value.replace(/\D/g, ''); // strip non-digits
                                                    field.onChange(onlyDigits === '' ? undefined : Number(onlyDigits));
                                                }}
                                            />
                                            <Input
                                                type="text"
                                                value={
                                                    field.value != null
                                                        ? formatNumber(Number(denom) * field.value, {
                                                              style: 'currency',
                                                              currency: parkingCurrency,
                                                          })
                                                        : `0.00 ${parkingCurrency === 'BGN' ? 'лв.' : '€'}`
                                                }
                                                disabled
                                            />
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormControlStack>
                            </FormItem>
                        )}
                    />
                );
            })}
        </>
    ) : null;
};

export default CashCollectionBreakdown;
